import { Formik, FormikProps } from 'formik'
import { AppEmailVerification } from '@/App'
import { useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useEffect } from 'react'
import { useCallback } from 'react'
import { FormLayout } from '@/components/Shared/FormLayout'
import {
    MidmarkButton,
    MidmarkFormikTextField,
} from '@midmark-enterprise/shared-components'
import { UserVerificationService } from '@/services/UserVerificationService/UserVerificationService'
import { useSimplePager } from '../Shared/SimplePager'
import ResendVerificationCodeLink from './ResendVerificationCodeLink'
import { useTranslation } from 'react-i18next'

type LocationState = {
    backUrl: string
    forwardUrl: string
}

type EmailVerificationFormProps = {
    emailVerification: Partial<AppEmailVerification>
    updateEmailVerification: (
        id: string | null,
        emailAddress: string,
        code?: string,
        callback?: () => void,
    ) => void
    isCompact: boolean
}

export default function EmailVerificationForm({
    emailVerification,
    updateEmailVerification,
}: EmailVerificationFormProps) {
    const { t } = useTranslation()
    const simplePager = useSimplePager()
    const verifyEmailVerificationCodeMutation =
        UserVerificationService.useVerifyEmailVerificationCodeMutation()

    const [formEmailVerification, setFormEmailVerification] =
        useState(emailVerification)
    const [formFilled, setFormFilled] = useState(false)

    const moveForward = useCallback(() => {
        simplePager.nextPage()
    }, [simplePager])

    const moveBackward = useCallback(() => {
        simplePager.previousPage()
    }, [simplePager])

    useEffect(() => {
        if (!formEmailVerification.id) {
            moveBackward()
        }
    }, [formEmailVerification.id, moveBackward])

    const submitForm = useCallback((props: FormikProps<any>) => {
        if (!props.isSubmitting) {
            props.submitForm()
        }
    }, [])

    const handleSubmit = useCallback(
        (
            values: { verifyEmail: string },
            action: {
                setFieldError: (fieldName: string, errorMessage: string) => void
                setSubmitting: (isSubmitting: boolean) => void
            },
        ) => {
            const code = values.verifyEmail.toUpperCase()

            if (!formEmailVerification.id) {
                throw new Error('Missing formEmailVerification.id')
            }

            if (!formEmailVerification.emailAddress) {
                throw new Error('Missing formEmailVerification.emailAddress')
            }

            verifyEmailVerificationCodeMutation
                .mutateAsync({
                    id: formEmailVerification.id,
                    code: code,
                    email: formEmailVerification.emailAddress,
                })
                .then((response) => {
                    setFormFilled(false)

                    action.setSubmitting(false)
                    if (response.isValid) {
                        if (!formEmailVerification.id) {
                            throw new Error('Missing formEmailVerification.id')
                        }

                        if (!formEmailVerification.emailAddress) {
                            throw new Error(
                                'Missing formEmailVerification.emailAddress',
                            )
                        }

                        updateEmailVerification(
                            formEmailVerification.id,
                            formEmailVerification.emailAddress,
                            code,
                        )

                        moveForward()
                    } else {
                        action.setFieldError(
                            'verifyEmail',
                            t('emailVerificationForm.emailVerificationForm.invalidCodeMessage')
                        )
                    }
                })
                .catch((error) => console.log(error))
        },
        [
            formEmailVerification.emailAddress,
            formEmailVerification.id,
            moveForward,
            updateEmailVerification,
            verifyEmailVerificationCodeMutation,
        ],
    )

    const validate = useCallback((values: { verifyEmail: string }) => {
        if (values.verifyEmail.length === 6) {
            setFormFilled(true)
        }
    }, [])

    const { emailAddress } = formEmailVerification
    const obfuscatedEmailAddress = useMemo(
        () => emailAddress?.replace(/^(.{2})[^@]+/, '$1***'),
        [emailAddress],
    )
    return (
        <Formik
            initialValues={{
                verifyEmail: '',
            }}
            validateOnChange={false}
            validateOnBlur={true}
            validate={validate}
            onSubmit={handleSubmit}
        >
            {(props) => (
                <>
                    <FormLayout
                        title={t('emailVerificationForm.emailVerificationForm.title')}
                        description={`${t('emailVerificationForm.emailVerificationForm.descriptionPart1')} ${obfuscatedEmailAddress}. ${t('emailVerificationForm.emailVerificationForm.descriptionPart2')}`}
                        buttons={
                            <>
                                <MidmarkButton
                                    onClick={moveBackward}
                                    data-testid={DATA_TESTIDS.backButton}
                                    color="secondary"
                                >
                                    {t('emailVerificationForm.emailVerificationForm.backButton')}
                                </MidmarkButton>
                                <MidmarkButton
                                    isSubmitting={props.isSubmitting}
                                    data-testid={DATA_TESTIDS.verifyButton}
                                    type="submit"
                                >
                                    {t('emailVerificationForm.emailVerificationForm.verifyButton')}
                                </MidmarkButton>
                            </>
                        }
                    >
                        <MidmarkFormikTextField
                            label={t('emailVerificationForm.emailVerificationForm.verificationCodeLabel')}
                            placeholder={t('emailVerificationForm.emailVerificationForm.verificationCodePlaceholder')}
                            formikProps={props}
                            autoComplete="off"
                            name="verifyEmail"
                            data-testid={DATA_TESTIDS.verificationCodeTextField}
                            sx={{
                                '& input': {
                                    textTransform: 'uppercase',
                                    '&::placeholder': {
                                        textTransform: 'none',
                                    },
                                },
                            }}
                        />

                        <ResendVerificationCodeLink
                            emailAddress={
                                formEmailVerification.emailAddress as string
                            }
                            formikProps={props}
                            onNewIdentificationGuid={(guid) => {
                                if (!formEmailVerification.emailAddress) {
                                    throw new Error('Missing email address')
                                }
                                updateEmailVerification(
                                    guid,
                                    formEmailVerification.emailAddress,
                                )
                                setFormEmailVerification({
                                    ...formEmailVerification,
                                    id: guid,
                                })
                            }}
                        />
                    </FormLayout>
                    {formFilled && submitForm(props)}
                </>
            )}
        </Formik>
    )
}

export const DATA_TESTIDS = {
    verifyButton: 'verify-button',
    backButton: 'back-button',
    verificationCodeTextField: 'verification-code-text-field',
    formikForm: 'formik-form',
}
